import type { UploadParams } from '../type';
import { httpRequest } from '../utils/httpRequest';
import {
  assertBucketName,
  assertFile,
  createCancelHandle,
  getUploadConcurrency,
  getUploadPartSize,
  resolveUploadFileName,
  shouldUseMultipartUpload,
} from '../utils/utils';

interface S3SdkInstance {
  getSignedUrl(operation: string, params: Record<string, unknown>): string;
  upload(
    params: Record<string, unknown>,
    callback: (err: unknown, result: unknown) => void
  ): unknown;
}

export async function upload(
  instance: unknown,
  params: UploadParams,
  bucketName?: string
): Promise<unknown> {
  const s3 = instance as S3SdkInstance;
  const file = assertFile(params.file);
  const resolvedBucketName = assertBucketName(bucketName);
  const fileName = resolveUploadFileName(params);
  const isMultipart = shouldUseMultipartUpload(file, params.maxSize);

  if (!isMultipart) {
    return uploadViaPresignedUrl(
      s3,
      file,
      fileName,
      resolvedBucketName,
      params
    );
  }

  return uploadViaMultipart(s3, file, fileName, resolvedBucketName, params);
}

async function uploadViaPresignedUrl(
  s3: S3SdkInstance,
  file: File,
  fileName: string,
  bucketName: string,
  params: UploadParams
): Promise<unknown> {
  const abortHandle = createCancelHandle(params.onCancel);
  const signedUrl = s3.getSignedUrl('putObject', {
    Bucket: bucketName,
    Key: fileName,
    Expires: 86400,
  });

  if (!signedUrl) {
    throw new Error('ObjectStorage: failed to generate S3 presigned URL');
  }

  return httpRequest({
    url: signedUrl,
    method: 'put',
    data: file,
    xhrInstance: abortHandle,
    onProgress: params.onUploadProgress,
  });
}

function uploadViaMultipart(
  s3: S3SdkInstance,
  file: File,
  fileName: string,
  bucketName: string,
  params: UploadParams
): Promise<unknown> {
  const partSize = getUploadPartSize(params.partSize);
  const concurrency = getUploadConcurrency(params.queueLength);

  return new Promise((resolve, reject) => {
    s3.upload(
      {
        Bucket: bucketName,
        Key: fileName,
        Body: file,
        PartSize: partSize,
        QueueSize: concurrency,
      },
      (err, result) => {
        if (err) {
          reject(err);
          return;
        }
        resolve(result);
      }
    );
  });
}
